import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { Layer } from 'bernie-view';

import { ILocalization } from '@shared-ui/localization-context';

import { SearchResultsStore } from 'stores/search-results-store';

import { UitkDialog, UitkDialogContent, UitkDialogFooter, UitkDialogFooterButtonProps } from 'uitk-react-dialog';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkHeading, UitkParagraph, UitkText } from 'uitk-react-text';
import { UitkIcon } from 'uitk-react-icons';
import { UitkTertiaryButton } from 'uitk-react-button';

import { handleLinkActionFromErrorMessage, handleRetry } from 'utils/error-handling-utils';

import { History, Location } from 'history';

export interface PackageErrorProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  l10n?: ILocalization;
}

export const PackageError: React.FC<PackageErrorProps> = React.memo(({ searchResults, analytics, l10n }) => {
  const { errorResponse, dialogErrorResponse } = searchResults;
  const history = useHistory();
  const location = useLocation();

  if (dialogErrorResponse) {
    return createDialogErrorResponse(searchResults, analytics, history, location);
  } else if (errorResponse) {
    return createErrorFromErrorResponse(searchResults, analytics, history, location);
  }

  return createGenericError(searchResults, analytics, history, location, l10n);
});

const createErrorFromErrorResponse = (
  searchResults: SearchResultsStore,
  analytics: AnalyticsStore,
  history: History,
  location: Location
) => {
  const errorResponse = searchResults.errorResponse;

  const errorRecoveryButtons = errorResponse.errorRecoveryButtons?.length ? (
    <UitkEmptyStateActionArea>
      {errorResponse.errorRecoveryButtons.map(error => (
        <UitkTertiaryButton
          key={error.primary}
          name="textonly"
          onClick={() => handleLinkActionFromErrorMessage(error.action, analytics, searchResults, history, location)}
          type="button"
        >
          {error.primary}
        </UitkTertiaryButton>
      ))}
    </UitkEmptyStateActionArea>
  ) : null;

  const defaultButton = (
    <UitkEmptyStateActionArea>
      <UitkTertiaryButton
        name="textonly"
        onClick={() => handleLinkActionFromErrorMessage(errorResponse.button.action, analytics, searchResults, history, location)}
        type="button"
      >
        {errorResponse.button.primary}
      </UitkTertiaryButton>
    </UitkEmptyStateActionArea>
  );

  return (
    <UitkEmptyState className="all-t-padding-three" data-stid={'multi-item-error-actions'}>
      <UitkIcon name={'search'} />
      <UitkHeading tag="h2">{errorResponse.header}</UitkHeading>
      <UitkText size={300} theme="default">
        {errorResponse.body}
      </UitkText>
      {errorRecoveryButtons}
      {defaultButton}
    </UitkEmptyState>
  );
};

const createGenericError = (
  searchResults: SearchResultsStore,
  analytics: AnalyticsStore,
  history: History,
  location: Location,
  l10n: ILocalization
) => {
  const headingText = l10n.formatText('multiItem.searchResults.error.text');
  const bodyText = l10n.formatText('multiItem.searchResults.tryAgain.text');
  const buttonText = l10n.formatText('multiItem.searchResults.retryButton.label');

  return (
    <UitkEmptyState className="all-t-padding-three" data-stid={'multi-item-generic-error'}>
      <UitkIcon name={'search'} />
      <UitkHeading tag="h2">{headingText}</UitkHeading>
      <UitkText size={300} theme="default">
        {bodyText}
      </UitkText>
      <UitkEmptyStateActionArea>
        <UitkTertiaryButton
          name="textonly"
          onClick={() => handleRetry(':errorOnSearch', 'MultiItem Retry Search', analytics, searchResults, history, location)}
          type="button"
        >
          {buttonText}
        </UitkTertiaryButton>
      </UitkEmptyStateActionArea>
    </UitkEmptyState>
  );
};

const createDialogErrorResponse = (searchResults: SearchResultsStore, analytics: AnalyticsStore, history: History, location: Location) => {
  const {
    body,
    button: { action, primary },
  } = searchResults.dialogErrorResponse;

  const buttonOne: UitkDialogFooterButtonProps = {
    onClick: () => {
      handleLinkActionFromErrorMessage(action, analytics, searchResults, history, location);
    },
    text: primary,
    type: 'tertiary',
  };

  return (
    <Layer id={'dialog-Error-Response-Layer'} data-stid={'dialog-error-response'}>
      <UitkDialog returnFocusOnClose={true}>
        <UitkDialogContent>
          <UitkParagraph size={2}>{body}</UitkParagraph>
        </UitkDialogContent>
        <UitkDialogFooter buttonOne={buttonOne} />
      </UitkDialog>
    </Layer>
  );
};
